import * as THREE from 'three';
import { REAR_VIEW_FOV, WorldCamera, type CameraPreset } from './camera';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { Avatar } from './avatar';
import type { Entity, WorldMap, Interaction } from './protocol';
import { socialPlayback } from './interaction-playback';
import { InteractionAssets } from './interaction-assets';
import type { PlacedObject } from './content';
import { ObjectRuntime } from './objects';
import { AssetCache } from './asset-cache';
import { AssetAvatar } from './asset-avatar';
import type { Member } from './identity';
import { HEIGHT } from './movement';
import { RoomLighting } from './room-lighting';
import { lightingPreset, type LightingProfile } from './lighting-profile';
import { GRAPHICS_PRESETS, effectiveGraphics, graphicsPreset, graphicsSchema, type GraphicsSettings, type GraphicsCapabilities } from './graphics-settings';
import { GraphicsPipeline, graphicsCapabilities } from './graphics-pipeline';
import { SunShadows } from './sun-shadows';
import { FrameBudget } from './frame-budget';
import { ReflectionProbes } from './reflection-probes';
import { probeKey } from './probe-keys';
import {forwardPlacement,placementAt,placementSurface} from './placement';
import type {ContentPlacement} from './service-contract/content-use';

export interface ContextTarget { kind: 'avatar' | 'object'; id: string }
/** Optional fixed view for the offline reference-scene capture harness. */
export interface FixedCameraView { position: [number, number, number]; target: [number, number, number] }

export class Viewport {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(REAR_VIEW_FOV, 1, .1, 150);
  private quality: GraphicsSettings;
  readonly graphicsCapabilities: GraphicsCapabilities;
  private pipeline: GraphicsPipeline | null = null;
  private sun: SunShadows;
  private lighting: RoomLighting;
  private lightingKey = '';
  private lightingSource = 'room';
  private drawingSize = new THREE.Vector2();
  readonly frameBudget = new FrameBudget();
  private probes: ReflectionProbes;
  private probeSceneRevision=-1;
  private mapRevision=0;
  private lastMapRevision=-1;
  private controls: WorldCamera;
  private selfID = '';
  private avatars = new Map<string, Avatar | AssetAvatar>();
  private nameTags = new Map<string, HTMLSpanElement>();
  private nameLayer = document.createElement('div');
  private nameAnchor = new THREE.Vector3();
  private nameRay = new THREE.Raycaster();
  readonly assets = new AssetCache();
  private interactionAssets = new InteractionAssets(this.assets);
  private statics = new THREE.Group();
  private observer: ResizeObserver;
  private map: WorldMap | null = null;
  private environment = 'flat-v1';
  private resetCameraPending = false;
  private entities: ReadonlyMap<string, Entity> = new Map();
  private objects = new ObjectRuntime(this.scene, this.assets);
  private transform: TransformControls;
  private draftRoot = new THREE.Object3D();
  private selection = new THREE.BoxHelper(this.draftRoot, 0x83e5c6);
  private selectedID = '';
  onSelect: (id: string) => void = () => {};
  onTransform: (position: THREE.Vector3, rotation: THREE.Quaternion, scale: THREE.Vector3) => void = () => {};
  private pick: (event: MouseEvent) => void;
  private contextEvents = new AbortController();
  private contextPointer: { id: number; x: number; y: number; dragged: boolean } | null = null;
  onContext: (target: ContextTarget | null, x: number, y: number) => void = () => {};
  get dragging(): boolean { return this.transform.dragging; }
  constructor(canvas: HTMLCanvasElement, quality: GraphicsSettings = { ...GRAPHICS_PRESETS.medium }) {
    this.quality = graphicsSchema.parse(quality);
    this.nameLayer.className = 'avatar-labels'; this.nameLayer.setAttribute('role', 'group'); this.nameLayer.setAttribute('aria-label', 'Nearby avatar names');
    canvas.parentElement!.append(this.nameLayer);
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace; this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.info.autoReset = false;
    this.graphicsCapabilities = graphicsCapabilities(this.renderer);
    this.assets.configure(this.renderer);
    this.renderer.shadowMap.enabled = true; this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.scene.background = new THREE.Color(0x182633); this.scene.fog = new THREE.Fog(0x182633, 24, 65);
    this.controls = new WorldCamera(this.camera, canvas);
    this.transform = new TransformControls(this.camera, canvas); this.scene.add(this.transform.getHelper(), this.draftRoot, this.selection); this.selection.visible = false;
    this.transform.addEventListener('dragging-changed', event => { this.controls.enabled = !event.value; });
    this.transform.addEventListener('objectChange', () => this.onTransform(this.draftRoot.position, this.draftRoot.quaternion, this.draftRoot.scale));
    this.pick = event => {
      const rect = canvas.getBoundingClientRect(), ray = new THREE.Raycaster();
      ray.setFromCamera(new THREE.Vector2((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1), this.camera);
      const id = this.objects.pick(ray); if (id) this.onSelect(id);
    };
    canvas.addEventListener('dblclick', this.pick);
    const contextOptions = { signal: this.contextEvents.signal };
    canvas.addEventListener('pointerdown', event => {
      this.contextPointer = event.button === 2 ? { id: event.pointerId, x: event.clientX, y: event.clientY, dragged: false } : null;
    }, contextOptions);
    canvas.addEventListener('pointermove', event => {
      const pointer = this.contextPointer;
      if (pointer?.id === event.pointerId && (Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 6 || (document.pointerLockElement === canvas && Math.hypot(event.movementX, event.movementY) > 6))) pointer.dragged = true;
    }, contextOptions);
    canvas.addEventListener('pointerup', event => {
      const pointer = this.contextPointer; this.contextPointer = null;
      if (event.button !== 2 || pointer?.id !== event.pointerId || pointer.dragged || this.dragging) return;
      const rect = canvas.getBoundingClientRect(), locked = document.pointerLockElement === canvas;
      const x = locked ? rect.left + rect.width / 2 : event.clientX, y = locked ? rect.top + rect.height / 2 : event.clientY;
      this.onContext(this.contextTarget(x, y), x, y);
    }, contextOptions);
    canvas.addEventListener('contextmenu', event => {
      event.preventDefault();
      // Keyboard context-menu invocation targets the center of the world view.
      if (event.button !== 2 && !this.contextPointer) {
        const rect = canvas.getBoundingClientRect(), x = rect.left + rect.width / 2, y = rect.top + rect.height / 2;
        this.onContext(this.contextTarget(x, y), x, y);
      }
    }, contextOptions);
    canvas.addEventListener('pointercancel', () => { this.contextPointer = null; }, contextOptions);
    this.sun = new SunShadows(this.scene);
    this.lighting = new RoomLighting(this.scene,this.renderer,this.sun,this.graphicsCapabilities.hdr);
    this.lighting.setProfile(lightingPreset('neutral'));
    this.probes=new ReflectionProbes(this.scene,this.renderer,this.graphicsCapabilities.hdr);
    this.probes.setProfile(this.lighting.profile);
    this.scene.add(this.statics);
    this.applyGraphicsSettings(this.quality);
    this.observer = new ResizeObserver(() => this.resize()); this.observer.observe(canvas);
    window.addEventListener('resize', () => {this.frameBudget.reset();this.resize();}, contextOptions);
  }
  get graphicsSettings(): GraphicsSettings { return { ...this.quality }; }
  setLightingProfile(profile?: LightingProfile, source = 'room'): void {
    this.lightingSource = source;
    const value = profile ?? lightingPreset('neutral'), key = JSON.stringify(value);
    if (key === this.lightingKey) return;
    this.lightingKey = key; this.lighting.setProfile(value);
    this.probes.setProfile(this.lighting.profile);
    this.frameBudget.suspendAdaptation(performance.now());
    this.rebuildPipeline(); this.applyGraphicsSettings(this.quality);
  }
  private rebuildPipeline(): void {
    this.pipeline?.dispose(); this.pipeline = null;
    const effective = effectiveGraphics(this.quality,this.graphicsCapabilities), bloom = this.lighting.profile.bloom;
    if (effective.antialiasing !== 'off' || effective.ambientOcclusion !== 'off' || (effective.bloom && bloom.strength > 0)) this.pipeline = new GraphicsPipeline(this.renderer,this.scene,this.camera,effective,this.graphicsCapabilities.hdr,bloom);
  }
  applyGraphicsSettings(value: GraphicsSettings): void {
    const settings = graphicsSchema.parse(value), before = effectiveGraphics(this.quality, this.graphicsCapabilities), effective = effectiveGraphics(settings, this.graphicsCapabilities);
    const rebuild = !this.pipeline || before.antialiasing !== effective.antialiasing || before.ambientOcclusion !== effective.ambientOcclusion || before.bloom !== effective.bloom;
    this.quality = settings;
    this.frameBudget.configure(settings);this.frameBudget.suspendAdaptation(performance.now());
    if (rebuild) this.rebuildPipeline();
    this.pipeline?.setAOStrength(effective.aoStrength);
    this.renderer.shadowMap.enabled = effective.shadowMapSize > 0 || settings.localShadows !== 'off';
    this.sun.configure(settings, effective.shadowMapSize);
    this.assets.setAnisotropy(effective.textureFiltering);
    this.camera.far = settings.viewDistance; this.camera.updateProjectionMatrix();
    this.lighting.setQuality(settings);
    this.probes.setQuality({...settings,reflections:effective.reflections,indirectLighting:effective.indirectLighting});
    this.resize();
  }
  private resize(): void {
    const canvas = this.renderer.domElement, width = canvas.clientWidth, height = canvas.clientHeight;
    if (!width || !height) return;
    const ratio = Math.min(devicePixelRatio, 2) * this.frameBudget.scale;
    if (this.renderer.getPixelRatio() !== ratio) this.renderer.setPixelRatio(ratio);
    this.renderer.setSize(width, height, false); this.camera.aspect = width / height; this.camera.updateProjectionMatrix();
    this.renderer.getDrawingBufferSize(this.drawingSize); this.pipeline?.resize(this.drawingSize.x, this.drawingSize.y);
  }
  beginFrame(now: number,visible=true): number | null {
    const before=this.frameBudget.scale, dt=this.frameBudget.tick(now,visible,!this.assets.busy&&!this.probes.pending);
    if(before!==this.frameBudget.scale) this.resize();
    return dt;
  }
  private contextTarget(x: number, y: number): ContextTarget | null {
    const rect = this.renderer.domElement.getBoundingClientRect(), ray = new THREE.Raycaster();
    ray.near = this.camera.near;
    ray.setFromCamera(new THREE.Vector2((x - rect.left) / rect.width * 2 - 1, -(y - rect.top) / rect.height * 2 + 1), this.camera);
    const object = this.objects.pickHit(ray);
    const hit = ray.intersectObjects([...this.avatars.values()].map(avatar => avatar.root), true)[0];
    const blocker = ray.intersectObjects(this.statics.children.filter(node => node instanceof THREE.Mesh), false)[0]?.distance ?? Infinity;
    if (object && object.distance < blocker && object.distance < (hit?.distance ?? Infinity)) return { kind: 'object', id: object.id };
    if (hit && hit.distance < blocker) {
      for (let node: THREE.Object3D | null = hit.object; node; node = node.parent) for (const [id, avatar] of this.avatars) if (avatar.root === node) return { kind: 'avatar', id };
    }
    return null;
  }
  placement(x?:number,y?:number):ContentPlacement|null {
    const roots=[...this.statics.children.filter(node=>node instanceof THREE.Mesh&&node.visible),...this.objects.roots];
    if(x===undefined||y===undefined)return forwardPlacement(this.entities.get(this.selfID),roots,this.camera.position);
    const rect=this.renderer.domElement.getBoundingClientRect();if(x<rect.left||x>rect.right||y<rect.top||y>rect.bottom)return null;
    const ray=new THREE.Raycaster();ray.setFromCamera(new THREE.Vector2((x-rect.left)/rect.width*2-1,-(y-rect.top)/rect.height*2+1),this.camera);
    return placementAt(placementSurface(ray,roots));
  }
  sync(entities: ReadonlyMap<string, Entity>, self: string, map: WorldMap | null, members: Member[] | null = null, environment = 'flat-v1'): void {
    this.entities = entities; this.selfID = self;
    if (map !== this.map || environment !== this.environment) { this.clearMap(); this.map = map; this.environment = environment;this.mapRevision++; if (map) this.buildMap(map); }
    for (const [id, avatar] of this.avatars) if (!entities.has(id)) { avatar.dispose(); this.avatars.delete(id); }
    for (const [id, tag] of this.nameTags) if (!entities.has(id) || id === self || !members?.some(member => member.entityId === id)) { tag.remove(); this.nameTags.delete(id); }
    for (const [id, entity] of entities) {
      const member = members?.find(member => member.entityId === id);
      let avatar = this.avatars.get(id);
      if (!avatar) { avatar = members ? new AssetAvatar(this.assets, id === self ? 0x83e5c6 : 0xeda976) : new Avatar(id === self ? 0x83e5c6 : 0xeda976); this.avatars.set(id, avatar); this.scene.add(avatar.root); }
      if (avatar instanceof AssetAvatar && member) avatar.setAppearance(member.appearance, member.animationStartMs);
      avatar.root.position.set(entity.position.x, entity.position.y, entity.position.z); avatar.root.quaternion.fromArray(entity.rotation);
      if (member && id !== self) {
        let tag = this.nameTags.get(id);
        if (!tag) { tag = document.createElement('span'); tag.className = 'avatar-name'; tag.dataset.entity = id; tag.hidden = true; this.nameLayer.append(tag); this.nameTags.set(id, tag); }
        if (tag.textContent !== member.displayName) tag.textContent = member.displayName;
      }
    }
  }
  private renderNames(): void {
    const canvas = this.renderer.domElement;
    for (const [id, tag] of this.nameTags) {
      const avatar = this.avatars.get(id)!, entity = this.entities.get(id)!;
      const head = avatar.root.getObjectByName('mHead') ?? avatar.root.getObjectByName('Head');
      if (head) { head.getWorldPosition(this.nameAnchor); this.nameAnchor.y += .4; }
      else this.nameAnchor.set(entity.position.x, entity.position.y + HEIGHT + .25, entity.position.z);
      const projected = this.nameAnchor.clone().project(this.camera);
      const inView = projected.z >= -1 && projected.z <= 1 && Math.abs(projected.x) < 1 && Math.abs(projected.y) < 1;
      if (!inView) { tag.hidden = true; continue; }
      // Test line of sight to the avatar's body, so tags don't reveal people through walls.
      const body = new THREE.Vector3(entity.position.x, entity.position.y + HEIGHT / 2, entity.position.z);
      const direction = body.sub(this.camera.position), distance = direction.length();
      this.nameRay.set(this.camera.position, direction.normalize()); this.nameRay.far = Math.max(0, distance - .05);
      tag.hidden = this.nameRay.intersectObjects(this.statics.children.filter(child => child instanceof THREE.Mesh), false).length > 0;
      if (!tag.hidden) { tag.style.left = `${(projected.x + 1) / 2 * canvas.clientWidth}px`; tag.style.top = `${(1 - projected.y) / 2 * canvas.clientHeight}px`; }
    }
  }
  private buildMap(map: WorldMap): void {
    const imported = this.environment === 'package-v1';
    if (!imported) {
      const floor = new THREE.Mesh(new THREE.BoxGeometry(map.bounds.max.x - map.bounds.min.x, .1, map.bounds.max.z - map.bounds.min.z), new THREE.MeshStandardMaterial({ color: 0x293f4d, roughness: 1 }));
      floor.position.set((map.bounds.min.x + map.bounds.max.x) / 2, -.05, (map.bounds.min.z + map.bounds.max.z) / 2); floor.receiveShadow = true; this.statics.add(floor);
      const grid = new THREE.GridHelper(24, 24, 0x526d7a, 0x344e5c); grid.position.y = .002; this.statics.add(grid);
    }
    for (const b of map.obstacles) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z), new THREE.MeshStandardMaterial({ color: 0x698398, roughness: .8 }));
      mesh.position.set((b.max.x + b.min.x) / 2, (b.max.y + b.min.y) / 2, (b.max.z + b.min.z) / 2); mesh.castShadow = true; mesh.receiveShadow = true; mesh.visible = !imported; this.statics.add(mesh);
    }
  }
  syncObjects(roomID: string, objects: readonly PlacedObject[]): void { this.objects.sync(roomID, objects); }
  get cameraPreset(): CameraPreset { return this.controls.preset; }
  setCameraPreset(preset: CameraPreset): void { this.controls.select(preset); }
  resetCamera(): void { this.controls.select('rear'); }
  environmentChanged(): void { this.resetCameraPending = true; this.controls.releasePointerLock(); }
  releasePointerLock(): void { this.controls.releasePointerLock(); }
  zoomCamera(factor: number): void { this.controls.zoom(factor); }
  movementDirection(forward: number) { return this.controls.movement(forward); }
  turnCamera(right: number, dt: number): void { if (!this.dragging) this.controls.turn(right, dt); }
  selectObject(id: string): void { this.selectedID = id; }
  editObject(value: PlacedObject['value'] | null): void {
    if (!value) { this.transform.dragging = false; this.transform.detach(); this.controls.enabled = true; return; }
    this.setDraft(value); this.transform.attach(this.draftRoot);
  }
  setDraft(value: PlacedObject['value']): void {
    this.draftRoot.position.set(value.position.x, value.position.y, value.position.z); this.draftRoot.quaternion.fromArray(value.rotation); this.draftRoot.scale.set(value.scale.x, value.scale.y, value.scale.z);
  }
  editMode(mode: 'translate' | 'rotate' | 'scale', snap: boolean): void {
    this.transform.setMode(mode); this.transform.setTranslationSnap(snap ? .25 : null); this.transform.setRotationSnap(snap ? Math.PI / 12 : null); this.transform.setScaleSnap(snap ? .1 : null);
  }
  private clearMap(): void {
    this.statics.traverse(object => {
      if (object instanceof THREE.Mesh || object instanceof THREE.LineSegments) {
        object.geometry.dispose(); const materials = Array.isArray(object.material) ? object.material : [object.material]; materials.forEach(m => m.dispose());
      }
    }); this.statics.clear();
  }
  interactionReadiness(interaction: Interaction): 'loading' | 'ready' | 'failed' {
    const assets = this.interactionAssets.readiness(interaction); if (assets !== 'ready') return assets;
    let ready = true;
    for (const role of interaction.roles) {
      const avatar = this.avatars.get(role.entityId);
      if (!(avatar instanceof AssetAvatar)) { ready = false; continue; }
      const state = avatar.diagnostics(); if (state.error) return 'failed';
      ready = ready && state.ready;
    }
    return ready ? 'ready' : 'loading';
  }
  syncInteractions(items: readonly Interaction[]): void { this.interactionAssets.sync(items); }
  render(dt: number, serverMS = 0, interactions: readonly Interaction[] = [], fixedView?: FixedCameraView): void {
    this.renderer.info.reset();
    const selected = this.objects.root(this.selectedID); this.selection.visible = !!selected;
    if (selected) this.selection.setFromObject(selected);
    for (const [id, avatar] of this.avatars) { if (avatar instanceof AssetAvatar) avatar.update(dt, this.entities.get(id)?.moving ?? false, serverMS, socialPlayback(interactions, id, serverMS, key => this.interactionAssets.bundle(key))); else avatar.update(dt, this.entities.get(id)?.moving ?? false); }
    this.controls.update(this.entities.get(this.selfID), this.map, dt);
    if (this.resetCameraPending && this.entities.has(this.selfID)) { this.controls.select('rear'); this.resetCameraPending = false; }
    if (fixedView) { this.camera.position.fromArray(fixedView.position); this.camera.lookAt(new THREE.Vector3().fromArray(fixedView.target)); }
    this.sun.update(this.camera, this.map);
    this.lighting.updateMaterials();
    if(this.probeSceneRevision!==this.objects.revision||this.lastMapRevision!==this.mapRevision) {
      this.probeSceneRevision=this.objects.revision;this.lastMapRevision=this.mapRevision;
      this.probes.setGeometryKey(probeKey({objects:this.objects.geometryKey,prototype:this.environment==='flat-v1'?this.map?.obstacles:null,bounds:this.environment==='flat-v1'?this.map?.bounds:null}));
    }
    this.probes.update(this.camera,[this.statics,...this.objects.roots],!this.assets.busy&&this.objects.ready&&!this.dragging);
    if (this.pipeline) this.pipeline.render(dt); else this.renderer.render(this.scene, this.camera);
    this.renderNames();
  }
  get diagnostics() {
    return { environment: this.environment, lighting: { ...this.lighting.diagnostics, source: this.lightingSource, probes:this.probes.diagnostics }, graphics: { requested: this.graphicsSettings, effective: effectiveGraphics(this.quality, this.graphicsCapabilities), preset: graphicsPreset(this.quality), capabilities: this.graphicsCapabilities, pixelRatio: this.renderer.getPixelRatio(), resolution: this.drawingSize.toArray(), aoResolution: this.pipeline?.aoResolution ?? null, shadows: this.sun.diagnostics, performance:this.frameBudget.diagnostics }, prototypeGeometry: this.statics.children.filter(node => node.visible).length, camera: this.controls.diagnostics, avatars: this.avatars.size, objects: this.objects.size, geometries: this.renderer.info.memory.geometries, textures: this.renderer.info.memory.textures, calls: this.renderer.info.render.calls, avatarAssets: [...this.avatars].map(([id, avatar]) => ({ id, visible: avatar.root.visible, ...(avatar instanceof AssetAvatar ? avatar.diagnostics() : { ready: true }) })), objectErrors: this.objects.errors, cache: this.assets.diagnostics };
  }
  async bakeIndirectProbes() {
    if(this.assets.busy||!this.objects.ready)throw new Error('Room assets are still loading');
    return this.probes.bake([this.statics,...this.objects.roots]);
  }
  resetAssets(): void { this.interactionAssets.reset(); for (const avatar of this.avatars.values()) avatar.dispose(); this.avatars.clear(); this.nameTags.clear(); this.nameLayer.replaceChildren(); this.objects.dispose(); this.assets.reset(); }
  dispose(): void {
    this.contextEvents.abort(); this.onContext = () => {};
    this.renderer.domElement.removeEventListener('dblclick', this.pick); this.transform.dispose(); this.selection.geometry.dispose(); this.selection.material.dispose();
    this.nameTags.clear(); this.nameLayer.remove();
    this.observer.disconnect(); this.controls.dispose(); for (const a of this.avatars.values()) a.dispose(); this.avatars.clear(); this.clearMap();
    this.objects.dispose();
    this.interactionAssets.reset();
    this.assets.shutdown();
    this.lighting.dispose();
    this.probes.dispose();
    this.pipeline?.dispose(); this.sun.dispose();
    this.renderer.dispose();
  }
}
